// Sentry 로 나가는 이벤트·브레드크럼에서 비밀을 지운다(api·web Worker·브라우저 한 벌) — 경로 토큰 · 헬스체크 핑 키 · 쿼리·조각 · 요청 머리글.

export const REDACTED = "[redacted]";

// /s/ 아래에서 토큰이 아닌 공개 주소(간편접수 slug·시공사례 code·추천 code·포트폴리오 slug) — 홈페이지에 걸리는 값이라 남긴다.
const SHARE_PUBLIC_HEADS = new Set(["intake", "p", "r", "portfolio"]);

// 경로 하나에서 비밀 자리만 바꾼다 — /s/<토큰> · /c/<토큰> · /invite/<토큰> · 앞에 /api 가 붙은 같은 모양 · /api/p/invite/<토큰>.
const redactOnePath = (path: string): string => {
  const parts = path.split("/");
  const api = parts[1] === "api";
  const at = api ? 2 : 1;
  const head = parts[at];
  const next = parts[at + 1];
  if (!next) return path;
  if (head === "c" || head === "invite" || (head === "s" && !SHARE_PUBLIC_HEADS.has(next))) parts[at + 1] = REDACTED;
  else if (api && head === "p" && next === "invite" && parts[at + 2]) parts[at + 2] = REDACTED;
  return parts.join("/");
};

// 쉼표·세미콜론·공백으로 이어 붙은 경로(「/s/a,/c/b」)는 하나씩 가린다.
const PATH_SEPARATORS = /([,;\s]+)/;

export const redactPath = (path: string): string =>
  path
    .split(PATH_SEPARATORS)
    .map((piece) => (piece.startsWith("/") ? redactOnePath(piece) : piece))
    .join("");

// 앞은 스킴 주소 또는 프로토콜 상대 주소(//host) · 쿼리·조각은 줄바꿈까지 포함해 끝까지.
const URL_PARTS = /^((?:[a-z][a-z0-9+.-]*:)?\/\/[^/?#]*)?([^?#]*)(\?[^#]*)?(#[\s\S]*)?$/i;

// 주소 하나(절대·상대) — 경로 토큰을 가리고 쿼리·조각(서명·로그인 토큰이 실릴 수 있다)은 통째로 뗀다 · hc-ping.com 은 첫 조각(핑 키)을 가린다.
export const redactUrl = (url: string): string => {
  const match = URL_PARTS.exec(url);
  if (!match) return url;
  const origin = match[1] ?? "";
  const rawPath = match[2] ?? "";
  const path = /^(?:https?:)?\/\/(?:[^/?#@]*@)?hc-ping\.com(?::\d+)?$/i.test(origin) ? rawPath.replace(/^\/[^/]+/, `/${REDACTED}`) : redactPath(rawPath);
  const tail = (match[3] ? `?${REDACTED}` : "") + (match[4] ? `#${REDACTED}` : "");
  return origin + path + tail;
};

// 글 속에 섞인 주소·경로(「GET /s/abc」, 오류 문구의 URL)를 찾아 가린다.
export const redactText = (text: string): string => text.replace(/(?:[a-z][a-z0-9+.-]*:\/\/[^\s"'<>,;]+|\/[^\s"'<>,;]*)/gi, (found) => redactUrl(found));

// 머리글은 user-agent 만 남기고 referer 는 주소를 가려 둔다 — authorization·cookie·x-buildpy-key 등 나머지는 전부 버린다.
const KEPT_HEADERS = new Set(["user-agent"]);
const URL_HEADERS = new Set(["referer"]);

const redactHeaders = (headers: Record<string, unknown>): Record<string, string> => {
  const kept: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    if (typeof value !== "string") continue;
    const lower = name.toLowerCase();
    if (KEPT_HEADERS.has(lower)) kept[name] = value;
    else if (URL_HEADERS.has(lower)) kept[name] = redactUrl(value);
  }
  return kept;
};

export interface ScrubbableBreadcrumb {
  category?: string;
  message?: string;
  data?: Record<string, unknown>;
}

export interface ScrubbableEvent {
  request?: { url?: string; headers?: Record<string, string>; query_string?: unknown; cookies?: unknown; data?: unknown };
  transaction?: string;
  message?: string;
  exception?: { values?: { value?: string }[] };
  breadcrumbs?: ScrubbableBreadcrumb[];
}

// 브레드크럼 data 의 주소 칸 — fetch·xhr 의 url · navigation 의 from·to.
const BREADCRUMB_URL_KEYS = ["url", "from", "to"];

export const scrubBreadcrumb = <T extends ScrubbableBreadcrumb>(breadcrumb: T): T => {
  if (typeof breadcrumb.message === "string") breadcrumb.message = redactText(breadcrumb.message);
  const data = breadcrumb.data;
  if (data) {
    // console 브레드크럼의 arguments 는 원본 Error·객체라 토큰 주소가 그대로 실린다 — message(가린 문자열)만 남긴다.
    if (breadcrumb.category === "console") delete data.arguments;
    for (const key of BREADCRUMB_URL_KEYS) {
      const value = data[key];
      if (typeof value === "string") data[key] = redactUrl(value);
    }
  }
  return breadcrumb;
};

// beforeSend 에서 부른다 — 이벤트를 제자리에서 고치고 그대로 돌려준다.
export const scrubEvent = <T extends ScrubbableEvent>(event: T): T => {
  const request = event.request;
  if (request) {
    if (typeof request.url === "string") request.url = redactUrl(request.url);
    if (request.headers) request.headers = redactHeaders(request.headers);
    delete request.query_string;
    delete request.cookies;
    delete request.data;
  }
  if (typeof event.transaction === "string") event.transaction = redactText(event.transaction);
  if (typeof event.message === "string") event.message = redactText(event.message);
  for (const value of event.exception?.values ?? []) {
    if (typeof value.value === "string") value.value = redactText(value.value);
  }
  for (const breadcrumb of event.breadcrumbs ?? []) scrubBreadcrumb(breadcrumb);
  return event;
};
